<template>
    <div class="absolute top-0 bottom-0 right-0 left-0 h-screen bg-gradient-to-br" :class="context.isDarkMode ? 'from-void to-black' : 'from-silver to-white'" v-if="!bodyLoaded"></div>

    <div class="content w-full flex flex-col pt-16 justify-center" :class="context.isDarkMode ? 'bg-void' : 'bg-white'" v-if="bodyLoaded">
        <LoadingDialog v-if="isLoading" :visible="isLoading" />

        <div v-if="clientModelValues.show" class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
            <Dialog v-model:visible="clientModelValues.show" class="mx-2 md:mx-20" :draggable="false"
                :closable="false" :pt:root:class="context.isDarkMode ? '!bg-void' : '!bg-white'" pt:mask:class="backdrop-blur-sm">
                <div class="rounded-lg p-6 text-center relative" :class="context.isDarkMode ? 'bg-void' : 'bg-white'">
                    <button class="absolute top-2 right-2 text-2xl md:block" :class="context.isDarkMode ? 'text-silver' : 'text-semidark-silver'"
                        @click="closeModal()">&times;</button>
                    
                    <Galleria 
                        containerStyle="max-width: 900px; margin-left: auto; margin-right: auto"
                        :value="clientModelValues.imageGallery" 
                        :responsiveOptions="responsiveGalleryOptions" 
                        :numVisible="4" 
                        :circular="true" 
                    >
                        <template #item="slotProps">
                            <img :src="slotProps.item.itemImageSrc" :alt="slotProps.item.alt" style="width: 100%; display: block" />
                        </template>
                        <template #thumbnail="slotProps">
                            <img v-if="slotProps.item.thumbnailImageSrc != ''" :src="slotProps.item.thumbnailImageSrc" :alt="slotProps.item.alt" class="w-1/2 mx-auto md:w-full block" />
                        </template>
                    </Galleria>

                    <p class="text-2xl my-5 text-left font-normal" :class="context.isDarkMode ? 'text-silver' : 'text-void'">
                        {{ translationHandler.translate(clientModelValues.title) }}
                    </p>
                    <p 
                        class="my-5 text-xl italic font-semibold text-left font-normal" :class="context.isDarkMode ? 'text-silver' : 'text-void'"
                        v-html="translationHandler.translate(clientModelValues.description)"
                    >
                    </p>
                </div>
            </Dialog>
        </div>

        <HeroComponent />

        <ServiceComponent />

        <AboutComponent />

        <CaseComponent @selectedItem="openCaseInformations" />

        <BenefitsComponent />

        <TestimonyComponent />

        <FaqComponent />

        <SecondHeroComponent />

        <ContactComponent @openLoader="showLoader" @showSuportModal="alertSuport" />

        <Dialog v-model:visible="showSupportInformations" :style="{ width: '90%' }" :draggable="false"
            :closable="false" :pt:root:class="context.isDarkMode ? '!bg-void' : '!bg-white'" pt:mask:class="backdrop-blur-sm">
            <div class="rounded-lg p-6 text-center relative" :class="context.isDarkMode ? 'bg-void' : 'bg-white'">
                <button class="absolute top-2 right-2 text-2xl md:block" :class="context.isDarkMode ? 'text-silver' : 'text-void'"
                        @click="resetSupportData()">&times;</button>
                <div class="flex flex-col items-center justify-center space-y-4" :class="context.isDarkMode ? 'text-silver' : 'text-void'">
                    <i class="pi pi-exclamation-triangle text-6xl text-red-500"></i>

                    <h2 class="text-2xl font-semibold">
                        {{ translationHandler.translate('An error occurred', 'ucfirst') }}
                    </h2>

                    <p>
                        {{ translationHandler.translate("Don't worry! Your attempt to submit your quote failed, but the form has been **safely saved** and we have already notified our support team.", 'ucfirst') }}
                        
                    </p>

                    <p class="text-sm font-light">
                        {{ translationHandler.translate('Our experts will take care of it and get back to you shortly.', 'ucfirst') }}
                    </p>
                </div>
            </div>
        </Dialog>
    </div>
</template>
<script setup>
import { useSessionMessageStore } from '@/stores/sessionMessage';
import { showToast } from '@/composables/toast';
import { Dialog, Galleria, useToast } from 'primevue';
import { onMounted, ref } from 'vue';
import translationHandler from '@/composables/handlers/TranslationHandler';
import { sleep } from '@/composables/sleep';
import DomHandler from '@/composables/handlers/DomHandler';
import LoadingDialog from '@/components/Utils/LoadingDialog.vue';
import { useContextStore } from "@/stores/context";
import HeroComponent from '@/components/HeroComponent.vue';
import ServiceComponent from '@/components/ServiceComponent.vue';
import AboutComponent from '@/components/AboutComponent.vue';
import CaseComponent from '@/components/CaseComponent.vue';
import TestimonyComponent from '@/components/TestimonyComponent.vue';
import BenefitsComponent from '@/components/BenefitsComponent.vue';
import FaqComponent from '@/components/FaqComponent.vue';
import SecondHeroComponent from '@/components/SecondHeroComponent.vue';
import ContactComponent from '@/components/ContactComponent.vue';
import SupportService from '@/services/SupportService';

const sessionMessageStore = useSessionMessageStore();
const context = useContextStore();
const isLoading = ref(false);
const bodyLoaded = ref(false);
const toast = useToast();

const clientModelValues = ref({
    title: '',
    image: '',
    imageGallery: [],
    description: '',
    show: false
});

onMounted(() => {
    watchForMessages();
    isBodyLoaded();
});

async function watchForMessages() {
    let awaitingTime = 500;
    let keepRolling = true; 
    while(keepRolling) {
        await sleep(awaitingTime);
        if (sessionMessageStore.hasMessage) {
            showToast(toast, sessionMessageStore.getCurrentMessage, sessionMessageStore.getMessageType);
            sessionMessageStore.cleanMessage;
            awaitingTime = awaitingTime * 4;
        }
    }
}

function openCaseInformations(selectedCase = null) {
    if (!selectedCase) {
        return;
    }

    const lang = context.getLang;

    let fullDesc = '';
    for (let key of Object.keys(selectedCase.fullDescription)) {
        const jsonData = selectedCase.fullDescription[key];
        fullDesc += jsonData[lang] + '<br><br>';
    }

    const data = {
        title: selectedCase.title,
        img: selectedCase.image,
        description: fullDesc,
        imageGallery: selectedCase.gallery
    }

    openModal(data, true);
}

async function isBodyLoaded() {
    await sleep(500);
    bodyLoaded.value = true;

    DomHandler.enableScroll();
}

function openModal(target = '') {
    let client = target;

    clientModelValues.value.title = client.title;
    clientModelValues.value.image = client.img;
    clientModelValues.value.description = client.description;
    clientModelValues.value.imageGallery = client.imageGallery ?? [];
    clientModelValues.value.show = true;

    DomHandler.disableScroll();
}

function closeModal() {
    clientModelValues.value.show = false; 
    DomHandler.enableScroll();
}

function showLoader(value) {
    const open = value == true;
    isLoading.value = open;
    if (open) {
        DomHandler.disableScroll();
    } else {
        DomHandler.enableScroll();
    }
}

const showSupportInformations = ref(false);
function alertSuport(dataPayload = {}) {
    showSupportInformations.value = true;

    SupportService.informSupportError(dataPayload);

    DomHandler.disableScroll();
}

function resetSupportData() {
    showSupportInformations.value = false;
    DomHandler.enableScroll();
}

const responsiveGalleryOptions = ref([
    {
        breakpoint: '1300px',
        numVisible: 4
    },
    {
        breakpoint: '575px',
        numVisible: 1
    }
]);

</script>

<style scoped>
.slide-enter-active,
.slide-leave-active {
    transition: all 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
    max-height: 0;
    opacity: 0;
    transform: translateY(-10px);
    overflow: hidden;
}

.slide-enter-to,
.slide-leave-from {
    max-height: 500px;
    opacity: 1;
    transform: translateY(0);
}
</style>